<!--src/views/ForgetPassword/ForgetPassword.vue-->
<template>
  <div class="forget-password-mask">
    <div class="forget-password-container">
      <button
        class="close-btn"
        @click="handleClose"
      >
        ×
      </button>
      <h2>忘记密码</h2>

      <!-- 内联提示文案（替代alert） -->
      <div
        class="tips-text"
        :class="tipsType"
      >
        {{ tipsText }}
      </div>

      <form
        class="forget-password-form"
        @submit="handleForgetPassword"
      >
        <!-- 手机号输入 -->
        <div class="form-group">
          <label for="phone">手机号</label>
          <input
            id="phone"
            v-model="phone"
            type="tel"
            name="phone"
            required
            placeholder="请输入11位手机号"
            maxlength="11"
            @input="clearTips"
          >
        </div>

        <!-- 验证码输入 -->
        <div class="form-group">
          <label for="code">验证码</label>
          <div class="code-input-wrapper">
            <input
              id="code"
              v-model="code"
              type="text"
              name="code"
              required
              placeholder="请输入6位验证码"
              maxlength="6"
              @input="clearTips"
            >
            <button
              type="button"
              class="get-code-btn"
              :disabled="!canSendCode"
              @click="getCode"
            >
              {{ codeText }}
            </button>
          </div>
        </div>

        <!-- 新密码输入 -->
        <div class="form-group">
          <label for="newPassword">新密码</label>
          <div class="input-wrapper">
            <input
              id="newPassword"
              class="pwd-input"
              :type="isPasswordVisible ? 'text' : 'password'"
              name="newPassword"
              required
              placeholder="请输入8-20位，含字母+数字/特殊符号！"
              @input="clearTips"
            >
            <span
              class="pwd-toggle"
              @click="togglePassword"
            >
              <text
                class="iconfont-showhide"
                :class="{'icon-yanjing_xianshi': isPasswordVisible, 'icon-yanjing_yincang_o': !isPasswordVisible}"
              />
            </span>
          </div>
        </div>

        <!-- 确认新密码 -->
        <div class="form-group">
          <label for="confirmPassword">确认新密码</label>
          <div class="input-wrapper">
            <input
              id="confirmPassword"
              class="pwd-input"
              :type="isConfirmPasswordVisible ? 'text' : 'password'"
              name="confirmPassword"
              required
              placeholder="请再次输入新密码"
              @input="clearTips"
            >
            <span
              class="pwd-toggle"
              @click="toggleConfirmPassword"
            >
              <text
                class="iconfont-showhide"
                :class="{'icon-yanjing_xianshi': isConfirmPasswordVisible, 'icon-yanjing_yincang_o': !isConfirmPasswordVisible}"
              />
            </span>
          </div>
        </div>

        <!-- 提交按钮 -->
        <button
          type="submit"
          class="submit-btn"
        >
          重置密码
        </button>

        <!-- 返回登录链接 -->
        <div class="back-login-link">
          记起密码了？
          <router-link
            to="/login"
            class="link"
          >
            返回登录
          </router-link>
        </div>
      </form>
    </div>
  </div>
</template>

<script setup>
import '@/assets/font/iconfont-showhide.css'
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { getSmsCode, forgetPassword } from '@/api/user'
// 引入校验工具
import { validatePhone, validatePassword, validateSmsCode } from '@/utils/validate'

const router = useRouter()

// 验证码相关状态
const codeDisabled = ref(false)
const codeText = ref('获取验证码')
let countdown = 60

// v-model 绑定字段
const phone = ref('')
const isPasswordVisible = ref(false)
const isConfirmPasswordVisible = ref(false)

const togglePassword = () => {
  isPasswordVisible.value = !isPasswordVisible.value
}
const toggleConfirmPassword = () => {
  isConfirmPasswordVisible.value = !isConfirmPasswordVisible.value
}
const code = ref('')
const newPassword = ref('')
const confirmPassword = ref('')

// 控制短信按钮是否可用
const canSendCode = computed(() => {
  const phoneReg = /^1[3-9]\d{9}$/
  return phoneReg.test(phone.value) && !codeDisabled.value
})

// 内联提示状态（替代alert）
const tipsText = ref('')
const tipsType = ref('') // success/warning/error

// 关闭页面（返回首页）
const handleClose = () => router.push('/')

// 清除提示文案
const clearTips = () => {
  tipsText.value = ''
  tipsType.value = ''
}

// 显示提示文案
const showTips = (text, type) => {
  tipsText.value = text
  tipsType.value = type
  // 成功提示3秒后自动清除
  if (type === 'success') {
    setTimeout(() => {
      clearTips()
    }, 3000)
  }
}

// 获取验证码
const getCode = async (e) => {
  if (e && e.preventDefault) e.preventDefault()
  const phoneVal = phone.value.trim()

  // 手机号校验（使用统一校验工具）
  const { valid: phoneValid, msg: phoneMsg } = validatePhone(phoneVal)
  if (!phoneValid) {
    showTips(phoneMsg, 'error')
    return
  }

  try {
    // 调用验证码接口，添加forget_password类型（后端需识别该类型）
    await getSmsCode({ phone: phoneVal, type: 'forget_password' })
    showTips('验证码已发送，请注意查收！', 'success')

    // 倒计时逻辑
    codeDisabled.value = true
    codeText.value = `${countdown}s后重新获取`
    const timer = setInterval(() => {
      countdown--
      codeText.value = `${countdown}s后重新获取`
      if (countdown <= 0) {
        clearInterval(timer)
        codeDisabled.value = false
        codeText.value = '获取验证码'
        countdown = 60
      }
    }, 1000)
  } catch (error) {
    // 优化验证码错误提示
    const errMsg = error.response?.data?.msg || '验证码发送失败，请稍后重试'
    showTips(errMsg, 'error')
    console.error('获取验证码失败：', error)
  }
}

// 提交重置密码
const handleForgetPassword = async (e) => {
  if (e && e.preventDefault) e.preventDefault()
  const phoneVal = phone.value.trim()
  const codeVal = code.value.trim()
  const newPasswordVal = newPassword.value.trim()
  const confirmPasswordVal = confirmPassword.value.trim()

  // 清除原有提示
  clearTips()

  // 1. 手机号校验
  const { valid: phoneValid, msg: phoneMsg } = validatePhone(phoneVal)
  if (!phoneValid) {
    showTips(phoneMsg, 'error')
    return
  }

  // 2. 验证码校验
  const { valid: codeValid, msg: codeMsg } = validateSmsCode(codeVal)
  if (!codeValid) {
    showTips(codeMsg, 'error')
    return
  }

  // 3. 新密码校验（使用统一复杂度规则）
  const { valid: pwdValid, msg: pwdMsg } = validatePassword(newPasswordVal)
  if (!pwdValid) {
    showTips(pwdMsg, 'error')
    return
  }

  // 4. 两次密码一致性校验
  if (newPasswordVal !== confirmPasswordVal) {
    showTips('两次输入的密码不一致！', 'error')
    return
  }

  try {
    // 调用忘记密码接口
    const res = await forgetPassword({ phone: phoneVal, code: codeVal, newPassword: newPasswordVal })
    showTips(res.msg || '密码重置成功！即将返回登录页', 'success')

    // 重置表单并跳转登录页
    // reset v-model values
    phone.value = ''
    code.value = ''
    newPassword.value = ''
    confirmPassword.value = ''
    setTimeout(() => {
      router.push('/login')
    }, 1500)
  } catch (error) {
    // 优化错误提示（优先提取后端返回信息）
    let errMsg = '密码重置失败，请稍后重试'
    if (error.response) {
      errMsg = error.response.data?.msg || `重置失败（${error.response.status}）`
    } else if (error.message.includes('Network')) {
      errMsg = '网络异常，请检查网络连接'
    }
    showTips(errMsg, 'error')
    console.error('重置密码失败：', error)
  }
}
</script>

<style lang="scss">
.forget-password-mask {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 9999;
  background: #faf3fc;
  display: flex;
  justify-content: center;
  align-items: center;
  overflow: hidden;
}
</style>

<style scoped lang="scss">
.forget-password-container {
  background: white;
  padding: 40px 30px;
  border-radius: 16px;
  box-shadow: 0 0 25px rgba(255, 100, 120, 0.1);
  width: 340px;
  position: relative;
  z-index: 10000;
}

// 内联提示样式（核心美化点）
.tips-text {
  height: 20px;
  line-height: 20px;
  font-size: 12px;
  margin-bottom: 10px;
  text-align: center;
  transition: all 0.3s ease;
}
.tips-text.success {
  color: #67c23a;
}
.tips-text.warning {
  color: #e6a23c;
}
.tips-text.error {
  color: #f56c6c;
}

h2 {
  text-align: center;
  color: #d92146;
  margin: 0 0 20px 0;
  font-size: 26px;
  font-weight: 600;
}

.close-btn {
  position: absolute;
  top: 15px;
  right: 15px;
  background: transparent;
  border: none;
  font-size: 22px;
  color: #ff99aa;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    color: #d92146;
    background: rgba(217, 33, 70, 0.08);
  }
}

.forget-password-form {
  display: flex;
  flex-direction: column;
  gap: 18px;
}

.pwd-toggle {
  position: absolute;
  right: 8px;
  top: 50%;
  transform: translateY(-50%);
  background: transparent;
  border: none;
  color: #d92146;
  font-size: 26px;
  cursor: pointer;
  padding: 0;
  width: 30px;
  height: 100%;
  max-height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.pwd-toggle::after {
  border: none;
}
.pwd-input {
  padding-right: 45px !important;
  box-sizing: border-box;
}
.iconfont-showhide {
  font-size: 20px;
}

.input-wrapper {
  position: relative;
  display: flex;
  align-items: center;
  flex: 1;
}
.form-group .input-wrapper input {
  padding-right: 45px !important;
}
.form-group {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    color: #666;
    font-size: 15px;
    font-weight: 500;
  }

  input {
    width: 100%;
    padding: 12px 15px;
    border: 1px solid #ffd6dc;
    border-radius: 8px;
    box-sizing: border-box;
    font-size: 15px;
    transition: all 0.3s ease;
    background-color: #fff9fa;

    &::placeholder {
      color: #ffb3c0;
    }

    &:focus {
      outline: none;
      border-color: #ff6b8b;
      box-shadow: 0 0 0 3px rgba(255, 107, 139, 0.1);
      background-color: white;
    }
  }
}

.code-input-wrapper {
  display: flex;
  gap: 10px;

  input {
    flex: 1;
  }

  .get-code-btn {
    width: 110px;
    padding: 12px 5px;
    background: #ff8e9f;
    color: white;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    cursor: pointer;
    transition: all 0.3s ease;

    &:disabled {
      background: #ffccd5;
      cursor: not-allowed;
      opacity: 0.8;
    }

    &:hover:not(:disabled) {
      background: #ff6b8b;
    }
  }
}

.submit-btn {
  padding: 13px;
  background: linear-gradient(135deg, #ff4d6d 0%, #d92146 100%);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 17px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  letter-spacing: 0.5px;
  margin-top: 10px;

  &:hover {
    opacity: 0.95;
    box-shadow: 0 4px 12px rgba(217, 33, 70, 0.2);
  }

  &:active {
    transform: scale(0.98);
  }
}

.back-login-link {
  text-align: center;
  font-size: 14px;
  color: #888;
  margin-top: 5px;

  .link {
    color: #d92146;
    text-decoration: none;
    margin-left: 4px;
    font-weight: 500;

    &:hover {
      color: #b31232;
      text-decoration: underline;
    }
  }
}

// 小屏适配优化
@media (max-width: 375px) {
  .forget-password-container {
    width: 90%;
    padding: 35px 20px;
  }

  .code-input-wrapper .get-code-btn {
    width: 90px;
    font-size: 12px;
  }

  .tips-text {
    font-size: 11px;
  }
}
</style>
